import { Grid } from '@mui/material';
import { useState } from 'react';
import { Outlet } from 'react-router-dom';

import { SmallTableEmptyState } from '@/components/display/SmallTableEmptyState/SmallTableEmptyState';
import { ButtonWithPopover } from '@/components/form/baseInputs/Button/ButtonWithPopover';
import { Gift01Icon } from '@/components/icons/Gift01Icon';
import { PlusIcon } from '@/components/icons/PlusIcon';
import { Card } from '@/components/layout/Card/Card';
import { MenuItem } from '@/components/poppers/MenuPopper/MenuItem';
import { useRequiredParam } from '@/hooks/useRequiredParam';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import { ExemptionTrackingTable } from '@/modules/gifting/ExemptionTrackingTable/ExemptionTrackingTable';
import { ExemptionTrackingTable_LifetimeExclusionEventFragment } from '@/modules/gifting/ExemptionTrackingTable/graphql/ExemptionTrackingTable.generated';
import { GiftCard } from '@/modules/gifting/GiftCard';
import { GiftDataCard } from '@/modules/gifting/GiftDataCard';
import { ClientDetailsGiftingPage_HouseholdFragment } from '@/modules/gifting/graphql/ClientDetailsGiftingPage.generated';
import { useClientDetailsGiftingPageQuery } from '@/modules/gifting/hooks/useClientDetailsGiftingPageQuery';
import { useGiftingExemptionsForClient } from '@/modules/gifting/hooks/useGiftingExemptionsForClient';
import { LifetimeExemptionCard } from '@/modules/gifting/LifetimeExemptionCard/LifetimeExemptionCard';
import { LogNewGiftModal } from '@/modules/gifting/LogNewGiftModal/LogNewGiftModal';
import { RecordExemptionUsedModal } from '@/modules/gifting/RecordExemptionUsedModal/RecordExemptionUsedModal';
import { COLORS } from '@/styles/tokens/colors';
import { formatCurrencyNoDecimals } from '@/utils/formatting/currency';

function NoGiftsCard({ householdId }: { householdId: string }) {
  const canEdit = useCanEditHouseholdData();
  const [isAddGiftModalOpen, setIsAddGiftModalOpen] = useState(false);
  const [isAddExemptionModalOpen, setIsAddExemptionModalOpen] = useState(false);

  return (
    <Card
      sx={{
        px: 2.5,
        py: 7,
      }}
      variant="outlined"
    >
      <SmallTableEmptyState
        icon={<Gift01Icon size={32} color={COLORS.TEXT.PRIMARY} />}
        heading="No gifts"
        description="Log an individual gift or record exemption used to get started"
        action={
          canEdit ? (
            <ButtonWithPopover
              label="Add"
              variant="primary"
              size="sm"
              startIcon={PlusIcon}
              popperVariant="menuBelow"
            >
              <MenuItem
                label="Log gift"
                onClick={() => setIsAddGiftModalOpen(true)}
              />
              <MenuItem
                label="Record exemption"
                onClick={() => setIsAddExemptionModalOpen(true)}
              />
            </ButtonWithPopover>
          ) : undefined
        }
      />
      {isAddGiftModalOpen && (
        <LogNewGiftModal
          householdId={householdId}
          isOpen={isAddGiftModalOpen}
          onClose={() => setIsAddGiftModalOpen(false)}
        />
      )}
      {isAddExemptionModalOpen && (
        <RecordExemptionUsedModal
          householdId={householdId}
          isOpen={isAddExemptionModalOpen}
          onClose={() => setIsAddExemptionModalOpen(false)}
        />
      )}
    </Card>
  );
}

interface Props {
  advisorClient?: ClientDetailsGiftingPage_HouseholdFragment;
  hasExclusionEvents: boolean;
  hasDsue: boolean;
  lifetimeExclusionEvents: ExemptionTrackingTable_LifetimeExclusionEventFragment[];
  householdId: string;
  loading: boolean;
}

function ClientDetailsGiftingPageInner({
  advisorClient,
  hasExclusionEvents,
  hasDsue,
  lifetimeExclusionEvents,
  householdId,
  loading,
}: Props) {
  const {
    gstExemptionSections,
    availableGstExemption,
    ctExemptionSections,
    availableCtExemption,
    hasCtGrantor,
  } = useGiftingExemptionsForClient({
    household: advisorClient,
    returnNegativeValues: false,
  });

  const showCtExemptionCard = hasCtGrantor && availableCtExemption;
  const itemWidth = showCtExemptionCard ? 4 : 6;

  return (
    <Grid container spacing={2} columns={12}>
      <Grid item xl={itemWidth} lg={itemWidth} sm={12}>
        <LifetimeExemptionCard householdId={householdId} />
      </Grid>
      {availableGstExemption && (
        <Grid item xl={itemWidth} lg={itemWidth} sm={12}>
          <GiftDataCard
            sections={gstExemptionSections}
            label="GST exemption"
            displayValue={formatCurrencyNoDecimals(availableGstExemption)}
          />
        </Grid>
      )}
      {showCtExemptionCard && (
        <Grid item xl={itemWidth} lg={itemWidth} sm={12}>
          <GiftDataCard
            sections={ctExemptionSections}
            label="CT exemption"
            displayValue={formatCurrencyNoDecimals(availableCtExemption)}
          />
        </Grid>
      )}
      {!hasExclusionEvents && !loading && (
        <Grid item sm={12}>
          <NoGiftsCard householdId={householdId} />
        </Grid>
      )}
      {(hasExclusionEvents || hasDsue) && (
        <Grid item sm={12}>
          <GiftCard>
            <ExemptionTrackingTable
              lifetimeExclusionEvents={lifetimeExclusionEvents}
              householdId={householdId}
              dsueClients={advisorClient?.possiblePrimaryClients ?? []}
            />
          </GiftCard>
        </Grid>
      )}
    </Grid>
  );
}

export function ClientDetailsGiftingPage() {
  const householdId = useRequiredParam('householdId');

  const clientDetailsGiftingPageState =
    useClientDetailsGiftingPageQuery(householdId);

  const {
    household: advisorClient,
    hasExclusionEvents,
    hasDsue,
    lifetimeExclusionEvents,
    loading,
  } = clientDetailsGiftingPageState;

  return (
    <>
      <ClientDetailsGiftingPageInner
        advisorClient={advisorClient}
        hasExclusionEvents={hasExclusionEvents}
        hasDsue={hasDsue}
        lifetimeExclusionEvents={lifetimeExclusionEvents}
        householdId={householdId}
        loading={loading}
      />
      <Outlet />
    </>
  );
}
